import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  CheckCircle,
  Copy,
  Send,
  Swords,
  Users,
} from "lucide-react";
import { createBattle, fetchBattles } from "../../../api/battleService";
import { fetchCategories } from "../../../api/categoryService";
import {
  fetchPremiumSocial,
  sendGameInvite,
} from "../../../api/premiumSocialService";
import ProfileSectionCard from "../components/ProfileSectionCard";
import { useProfileHub } from "../ProfileHubContext";

const defaultForm = {
  category: "",
  date: "",
  time: "",
  roundCount: "3",
  message: "",
};

function formatBattleDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function battleLink(battleId) {
  if (typeof window === "undefined") {
    return `/battles/${battleId}`;
  }
  return `${window.location.origin}/battles/${battleId}`;
}

export default function ProfileBattlesSection() {
  const { profileIdentity } = useProfileHub();
  const [categories, setCategories] = useState([]);
  const [social, setSocial] = useState({ friends: [] });
  const [battles, setBattles] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [inviteSelections, setInviteSelections] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const acceptedFriends = useMemo(
    () => (social.friends || []).filter((item) => item.status === "accepted"),
    [social.friends],
  );

  const myBattles = useMemo(
    () =>
      (battles || []).filter(
        (battle) => String(battle.creator?.id) === String(profileIdentity.id),
      ),
    [battles, profileIdentity.id],
  );

  const refreshData = async (signal) => {
    const [categoryData, battleData, socialData] = await Promise.all([
      fetchCategories(signal),
      fetchBattles({}, signal),
      fetchPremiumSocial(signal),
    ]);
    const nextCategories = Array.isArray(categoryData) ? categoryData : [];
    setCategories(nextCategories);
    setBattles(Array.isArray(battleData?.battles) ? battleData.battles : []);
    setSocial(socialData || { friends: [] });
    if (nextCategories[0]?.slug) {
      setForm((current) =>
        current.category ? current : { ...current, category: nextCategories[0].slug },
      );
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    refreshData(controller.signal)
      .catch(() => setError("Chargement des battles impossible."))
      .finally(() => setIsLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleFriend = (friendId) => {
    setSelectedFriends((current) =>
      current.includes(friendId)
        ? current.filter((item) => item !== friendId)
        : [...current, friendId],
    );
  };

  const toggleInviteFriend = (battleId, friendId) => {
    setInviteSelections((current) => {
      const selected = current[battleId] || [];
      return {
        ...current,
        [battleId]: selected.includes(friendId)
          ? selected.filter((item) => item !== friendId)
          : [...selected, friendId],
      };
    });
  };

  const copyBattleLink = async (battleId) => {
    const link = battleLink(battleId);
    try {
      await navigator.clipboard.writeText(link);
      setMessage("Lien d'invitation copie.");
      setError("");
    } catch {
      setMessage(link);
      setError("");
    }
  };

  const inviteFriendsToBattle = async (battleId, friendIds, customMessage = "") => {
    if (!friendIds.length) {
      return 0;
    }
    await Promise.all(
      friendIds.map((friendId) =>
        sendGameInvite({
          receiverId: friendId,
          battleId,
          message: customMessage,
        }),
      ),
    );
    return friendIds.length;
  };

  const handleCreateBattle = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage("");
    setError("");

    try {
      const startsAt = new Date(`${form.date}T${form.time}`).toISOString();
      const response = await createBattle({
        category: form.category,
        starts_at: startsAt,
        round_count: Number(form.roundCount),
        description: form.message,
      });
      const battle = response.battle;
      const invitedCount = await inviteFriendsToBattle(
        battle.id,
        selectedFriends,
        form.message,
      );
      setMessage(
        invitedCount
          ? `Battle creee et ${invitedCount} invitation(s) envoyee(s).`
          : "Battle creee.",
      );
      setForm(defaultForm);
      setSelectedFriends([]);
      await refreshData();
    } catch (submitError) {
      setError(
        submitError?.response?.data?.error ||
          "Creation de battle impossible.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInviteExisting = async (battleId) => {
    const friendIds = inviteSelections[battleId] || [];
    setMessage("");
    setError("");
    try {
      const count = await inviteFriendsToBattle(battleId, friendIds);
      setInviteSelections((current) => ({ ...current, [battleId]: [] }));
      setMessage(`${count} invitation(s) envoyee(s).`);
      await refreshData();
    } catch (inviteError) {
      setError(
        inviteError?.response?.data?.error ||
          "Invitation battle impossible.",
      );
    }
  };

  return (
    <div className="space-y-6">
      <ProfileSectionCard
        title="Battles"
        subtitle="Crée une battle et invite tes amis"
      >
        {message ? (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleCreateBattle} className="grid gap-4 lg:grid-cols-2">
          <label className="text-sm font-semibold text-slate-700">
            Catégorie
            <select
              value={form.category}
              onChange={(event) => updateForm("category", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
              required
            >
              {categories.map((category) => (
                <option key={category.slug} value={category.slug}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Durée
            <select
              value={form.roundCount}
              onChange={(event) => updateForm("roundCount", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
            >
              <option value="3">3 parties consécutives</option>
              <option value="5">5 parties consécutives</option>
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Date
            <input
              type="date"
              value={form.date}
              onChange={(event) => updateForm("date", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
              required
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Heure
            <input
              type="time"
              value={form.time}
              onChange={(event) => updateForm("time", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
              required
            />
          </label>
          <label className="text-sm font-semibold text-slate-700 lg:col-span-2">
            Message d'invitation
            <textarea
              value={form.message}
              onChange={(event) => updateForm("message", event.target.value)}
              rows={3}
              maxLength={255}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900"
              placeholder="Viens tenter la battle avec moi."
            />
          </label>

          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
            <div className="flex items-center gap-2 text-sm font-black text-slate-900">
              <Users className="h-4 w-4" />
              Inviter des amis acceptés
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {acceptedFriends.map((item) => (
                <label
                  key={item.friend.id}
                  className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-slate-700"
                >
                  <input
                    type="checkbox"
                    checked={selectedFriends.includes(item.friend.id)}
                    onChange={() => toggleFriend(item.friend.id)}
                  />
                  {item.friend.username}
                </label>
              ))}
              {acceptedFriends.length === 0 ? (
                <div className="text-sm text-slate-500">Aucun ami accepte pour le moment.</div>
              ) : null}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-black uppercase tracking-[0.16em] text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Swords className="h-4 w-4" />
            Creer la battle
          </button>
        </form>
      </ProfileSectionCard>

      <ProfileSectionCard title="Mes battles" subtitle="Lien partageable et invitations">
        <div className="space-y-4">
          {myBattles.map((battle) => {
            const selected = inviteSelections[battle.id] || [];
            return (
              <div key={battle.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                      <CalendarClock className="h-4 w-4 text-cyan-600" />
                      {battle.title}
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      {battle.category?.name} - {formatBattleDate(battle.starts_at)} - {battle.round_count} parties
                    </div>
                    <div className="mt-2 text-xs font-semibold text-slate-500">
                      {battle.participants_count} participe(nt), {battle.declined_count} refuse(nt)
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/battles/${battle.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
                    >
                      Voir
                    </Link>
                    <button
                      type="button"
                      onClick={() => copyBattleLink(battle.id)}
                      className="inline-flex items-center gap-2 rounded-full bg-cyan-500 px-3 py-2 text-xs font-bold text-white"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copier le lien
                    </button>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl bg-slate-50 px-3 py-3">
                  <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                    Inviter a cette battle
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {acceptedFriends.map((item) => (
                      <label
                        key={`${battle.id}-${item.friend.id}`}
                        className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(item.friend.id)}
                          onChange={() => toggleInviteFriend(battle.id, item.friend.id)}
                        />
                        {item.friend.username}
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    disabled={!selected.length}
                    onClick={() => handleInviteExisting(battle.id)}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {selected.length ? <Send className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
                    Envoyer invitation
                  </button>
                </div>
              </div>
            );
          })}
          {!myBattles.length ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
              Aucune battle créée depuis ton compte pour le moment.
            </div>
          ) : null}
        </div>
      </ProfileSectionCard>
    </div>
  );
}
